import React, { useEffect, useState } from 'react';
import Stepper, { StepperType } from '@dealroadshow/uikit/core/components/Stepper';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import StepFormContextProvider, {
  PortfolioFormSteps,
  useStepFormContext,
} from '@/finsight/application/PortfolioMonitor/StepFormContext';
import { CreateListsForm, NamePortfolioForm, OptionalData, ReviewUnknowns } from './FormSteps';
import FormPayloadContextProvider from '@/finsight/application/PortfolioMonitor/FormPayloadContext';
import { usePortfolioMonitorContext } from '@/finsight/application/PortfolioMonitor/PortfolioMonitorContext';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import useCancelChangesModal from '@/dataroom/ui/common/Questions/CancelChangesModal/useCancelChangesModal';

import styles from './formStyles.scss';

const PortfolioForm = () => {
  const { currentStep, setCurrentStep } = useStepFormContext();
  const { isFormOpened, setIsFormOpened, currencies: { getCurrenciesList, collection } } = usePortfolioMonitorContext();
  const { filledPortfolio, emptyPortfolio, selectedPortfolio, portfolioCollection, isLoading } = usePortfoliosContext();
  const [isAnyChanges, setIsAnyChanges] = useState<boolean>(false);

  const {
    CancelChangesModal,
    showCanceledModal,
    hideCanceledModal,
    isVisible: isCancelChangesVisible,
  } = useCancelChangesModal();

  const cancelEditing = () => {
    if (portfolioCollection.length > 1) {
      setCurrentStep(PortfolioFormSteps.NAME_PORTFOLIO);
    }
    setIsFormOpened(false);
  };

  const handleCancelButtonClick = () => {
    if (isAnyChanges) {
      showCanceledModal();
    } else {
      cancelEditing();
    }
  };

  const confirmCancelChanges = () => {
    hideCanceledModal();
    cancelEditing();
  };

  const renderStep = (step: PortfolioFormSteps) => {
    const stepToSubform = {
      [PortfolioFormSteps.NAME_PORTFOLIO]: <NamePortfolioForm />,
      [PortfolioFormSteps.COPY_AND_PASTE_CUSIPS]:
  <CreateListsForm setIsAnyChanges={ setIsAnyChanges } cancelButtonClick={ handleCancelButtonClick } />,
      [PortfolioFormSteps.REVIEW_UNKNOWNS]:
  <ReviewUnknowns setIsAnyChanges={ setIsAnyChanges } cancelButtonClick={ handleCancelButtonClick } />,
      [PortfolioFormSteps.ADD_OPTIONAL_DATA]:
  <OptionalData setIsAnyChanges={ setIsAnyChanges } cancelButtonClick={ handleCancelButtonClick } />,
    };
    return stepToSubform[step];
  };

  const portfolioSteps = [
    { url: PortfolioFormSteps.NAME_PORTFOLIO, title: 'Name Portfolio', dataTest: 'namePortfolio' },
    { url: PortfolioFormSteps.COPY_AND_PASTE_CUSIPS, title: 'Copy & Paste Cusips', dataTest: 'copyPasteCusips' },
    { url: PortfolioFormSteps.REVIEW_UNKNOWNS, title: 'Review Unknowns', dataTest: 'reviewUnknowns' },
    { url: PortfolioFormSteps.ADD_OPTIONAL_DATA, title: 'Add Optional Data', dataTest: 'addOptionalData' },
  ];

  useEffect(() => {
    if (filledPortfolio || emptyPortfolio) {
      setCurrentStep(PortfolioFormSteps.COPY_AND_PASTE_CUSIPS);
    } else {
      setCurrentStep(PortfolioFormSteps.NAME_PORTFOLIO);
    }
  }, [filledPortfolio, selectedPortfolio]);

  useEffect(() => {
    if (!collection.length) {
      getCurrenciesList();
    }
  }, [collection.length]);

  return isFormOpened && (
    <>
      <Stepper currentStep={ currentStep } steps={ portfolioSteps } stepperType={ StepperType.M } />
      { isLoading && <Spinner isVisible overlay isAbsolute /> }
      { renderStep(currentStep) }
      <CancelChangesModal
        isVisible={ isCancelChangesVisible }
        hideCanceledModal={ hideCanceledModal }
        cancelChanges={ confirmCancelChanges }
        discardButtonClassName={ styles.discardButton }
        title="Yes"
        modalText="Are you sure you want to cancel? Any changes made will be lost."
      />
    </>
  );
};

export default ({ isEditing }) => (
  <FormPayloadContextProvider isEditing={ isEditing }>
    <StepFormContextProvider isEditing={ isEditing }>
      <PortfolioForm />
    </StepFormContextProvider>
  </FormPayloadContextProvider>
);
